// frontend/src/components/HeroSection.jsx
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, ShieldCheck, BookOpen, ChevronRight, MessageSquare } from 'lucide-react';

/**
 * 홈페이지 히어로 섹션 컴포넌트 - 심플한 디자인
 */
const MOBILE_QUICK_NAV = [
  {
    to: '/safety-regulations',
    label: '안전법규',
    description: '법령·해석 조회',
    icon: BookOpen,
    accent: 'bg-blue-600',
    light: 'bg-blue-50 text-blue-700',
  },
  {
    to: '/safety-materials',
    label: '안전자재',
    description: '품목별 가이드',
    icon: ShieldCheck,
    accent: 'bg-emerald-600',
    light: 'bg-emerald-50 text-emerald-700',
  },
  {
    to: '/board',
    label: '자유게시판',
    description: '안전 이슈·정보 공유',
    icon: MessageSquare,
    accent: 'bg-amber-500',
    light: 'bg-amber-50 text-amber-700',
  },
];

const HeroSection = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  // 검색 제출 핸들러
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  return (
    <div className="relative">
      {/* 배경 이미지 - 줌아웃 효과 적용 */}
      <div 
        className="absolute inset-0 overflow-hidden z-0"
        style={{ 
          backgroundImage: 'url("/assets/HeroSection_Logo.jpg")',
          backgroundSize: '80%',
          backgroundPosition: 'center 28%',
          backgroundRepeat: 'no-repeat',
        }}
      ></div>
      
      {/* 콘텐츠 */}
      <div className="relative z-10 min-h-0 md:min-h-[420px]">
        <div className="absolute inset-0 bg-gradient-to-b from-navy-900/85 via-navy-900/45 to-navy-900/25" />

        {/* 상단 정렬 — 캐릭터 얼굴(중앙)과 겹치지 않게 */}
        <div className="relative z-10 container mx-auto px-4 sm:px-6 flex flex-col md:min-h-[420px] pt-5 sm:pt-8 md:pt-10 pb-5 sm:pb-6 text-center">
          <div>
            <p className="hidden sm:inline-flex items-center gap-2 px-4 py-1.5 mb-4 rounded-full bg-white/10 border border-gold-300/40 text-sm sm:text-base text-gold-100 backdrop-blur-sm">
              <ShieldCheck size={18} className="shrink-0 text-gold-300" aria-hidden />
              공신력 있는 안전 법률·해석 통합 검색
            </p>
            <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-bold drop-shadow-lg text-white leading-tight mb-2 sm:mb-4">
              안전관리, 궁금한 것은<br className="sm:hidden" /> 무엇이든 검색하세요
            </h1>
            <p className="hidden sm:block text-base sm:text-lg md:text-xl text-gray-100 drop-shadow-md max-w-3xl mx-auto leading-relaxed">
              <strong className="font-semibold text-white">법률·시행령 등 법령 정보</strong>와
              <strong className="font-semibold text-white"> 법령 해석 자료</strong>를
              연결해 한곳에서 알려드립니다.
            </p>
          </div>

          <div className="hidden md:block flex-1 min-h-8 md:min-h-10" aria-hidden="true" />
          <div className="md:hidden h-3" aria-hidden="true" />

          {/* 검색창 */}
          <div className="max-w-2xl w-full mx-auto bg-white/10 backdrop-blur-sm p-3 sm:p-6 rounded-2xl border border-gold-300/30">
            <form onSubmit={handleSearchSubmit} className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="예: 안전모 착용, 산업안전보건관리비, 중대재해처벌법…"
                aria-label="안전관리 통합 검색"
                className="w-full px-4 py-4 pl-12 pr-20 rounded-xl border border-gray-300 focus:ring-2 focus:ring-gold-400 focus:border-gold-400 text-gray-800 bg-white/90 text-base sm:text-lg placeholder:text-gray-500"
              />
              <Search 
                className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500"
                size={24}
              />
              <button
                type="submit"
                className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-navy-700 text-white px-6 py-2 rounded-lg hover:bg-navy-600 transition border border-gold-300/50 font-semibold"
              >
                검색
              </button>
            </form>
          </div>

          {/* 모바일 빠른 메뉴 */}
          <nav
            className="md:hidden mt-4 w-full max-w-2xl mx-auto grid grid-cols-1 gap-3 text-left"
            aria-label="주요 메뉴"
          >
            {MOBILE_QUICK_NAV.map(({ to, label, description, icon: Icon, accent, light }) => (
              <Link
                key={to}
                to={to}
                className="flex items-center gap-4 px-4 py-4 rounded-xl bg-white shadow-md border border-white/80 active:scale-[0.98] transition-transform"
              >
                <div className={`flex items-center justify-center w-12 h-12 rounded-xl shrink-0 ${light}`}>
                  <Icon size={24} strokeWidth={2.25} aria-hidden />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-lg font-bold text-gray-900 leading-tight">{label}</p>
                  <p className="text-sm text-gray-500 mt-0.5">{description}</p>
                </div>
                <div className={`flex items-center justify-center w-8 h-8 rounded-full shrink-0 ${accent} text-white`}>
                  <ChevronRight size={18} strokeWidth={2.5} aria-hidden />
                </div>
              </Link>
            ))}
          </nav>
        </div>
      </div>
    </div>
  );
};

export default HeroSection;